import { Circle } from "lucide-react";
import { useEffect, useState } from "react";
import { Check } from "@/components/animate-ui/icons/check";
import { X } from "@/components/animate-ui/icons/x";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { authClient } from "@/lib/api/auth-client";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import { useOnboardingStore } from "@/stores/onboarding-store";

/** Persist checklist dismissed to backend and update auth store. */
async function persistDismiss() {
	const { data } = await authClient.PATCH("/api/v1/auth/me", {
		body: { checklist_dismissed: true },
	});
	if (data) {
		const current = useAuthStore.getState().user;
		if (current) {
			useAuthStore.setState({ user: { ...current, checklist_dismissed: true } });
		}
	}
}

export function ChecklistWidget() {
	const { checklist, dismissChecklist, toggleChecklistItem } = useOnboardingStore();
	const user = useAuthStore((state) => state.user);
	const [showCelebration, setShowCelebration] = useState(false);
	const [fadeOut, setFadeOut] = useState(false);

	const handleClose = () => {
		setShowCelebration(true);
		setTimeout(() => setFadeOut(true), 1000);
		setTimeout(() => {
			dismissChecklist();
			persistDismiss();
		}, 1500);
	};

	// Calculate completion
	const completedCount = checklist.filter((item) => item.completed).length;
	const totalCount = checklist.length;
	const progressPercent = (completedCount / totalCount) * 100;
	const allCompleted = completedCount === totalCount && totalCount > 0;

	// Trigger celebration animation when all items are completed
	useEffect(() => {
		if (allCompleted && !showCelebration) {
			setShowCelebration(true);
			setTimeout(() => setFadeOut(true), 1000);
			setTimeout(() => {
				dismissChecklist();
				persistDismiss();
			}, 1500);
		}
	}, [allCompleted, showCelebration, dismissChecklist]);

	// Don't render if dismissed (backend-driven)
	if (user?.checklist_dismissed) {
		return null;
	}

	return (
		<Card
			className={cn(
				"border-border/20 bg-muted/30 relative transition-opacity duration-500",
				fadeOut && "opacity-0"
			)}
		>
			{/* Celebration overlay */}
			{showCelebration && (
				<div className="absolute inset-0 flex items-center justify-center bg-muted/80 rounded-lg z-10 animate-in fade-in duration-300">
					<div className="flex size-16 items-center justify-center rounded-full bg-primary text-primary-foreground animate-in zoom-in duration-300">
						<Check className="size-10" strokeWidth={3} />
					</div>
				</div>
			)}

			<CardHeader className="pb-2">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<CardTitle className="text-xs font-medium text-muted-foreground">
							Getting Started
						</CardTitle>
						<span className="text-xs text-muted-foreground">
							{completedCount}/{totalCount}
						</span>
					</div>
					<button
						type="button"
						onClick={handleClose}
						className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
						aria-label="Close checklist"
					>
						<X animateOnHover className="size-3.5" />
					</button>
				</div>
				<Progress value={progressPercent} className="h-1" />
			</CardHeader>

			<CardContent className="space-y-2">
				{/* Checklist items */}
				<div className="space-y-1.5">
					{checklist.map((item) => (
						<button
							type="button"
							key={item.id}
							className="flex items-start gap-2 cursor-pointer rounded px-1 py-0.5 -mx-1 hover:bg-muted/50 transition-colors w-full text-left"
							onClick={() => toggleChecklistItem(item.id)}
						>
							{/* Checkbox icon */}
							<div className="mt-0.5">
								{item.completed ? (
									<div className="flex size-3 items-center justify-center rounded-full bg-primary/80 text-primary-foreground">
										<Check className="size-2" strokeWidth={3} />
									</div>
								) : (
									<Circle className="size-3 text-muted-foreground/60" />
								)}
							</div>

							{/* Label */}
							<p
								className={cn(
									"flex-1 text-xs leading-tight",
									item.completed ? "text-muted-foreground/60 line-through" : "text-muted-foreground"
								)}
							>
								{item.label}
							</p>
						</button>
					))}
				</div>
			</CardContent>
		</Card>
	);
}
